import { LitElement, html } from "lit-element";
import { until } from "lit-html/directives/until.js";
import { getImageById, getDrawingById } from "../data/images.js";
import { deleteForever, brush, image, description } from "../styles/icons.js";
import { SharedStyles } from "../styles/shared-styles.js";
import "../components/inspx-photo-viewer.js";
import { InspxDialog } from "./inspx-dialog.js";
import { ImageSettingsPopup } from "./inspx-image-settings-popup.js";

class InspxDbImage extends LitElement {
	shouldUpdate() {
		return this.src != undefined;
	}

	render() {
		return html`
            ${SharedStyles}
            <style>
                :host {
                    position: relative;
                    text-align: center;
                    height: var(--db-image-height, 200px);
                }
            
                .img {
                    height: var(--db-image-height, 200px);
                    min-height: 200px;
                    background-size: cover;
                    background-repeat: no-repeat;
                    background-position: center center;
                }
            
                .f {
                    display: block;
                    padding: 4px 8px;
                    background-color: var(--primary-color-variant3);
                    width: inherit;
                    height: 56px;
                    margin-top: -56px;
                    color: var(--secondary-color);
                    opacity: 0.9;
                    box-sizing: border-box;
                }

                .img-name {
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    text-align: left;
                    min-height: 24px;
                }

                .img-options {
                    text-overflow: ellipsis;
                    white-space: nowrap;
                    overflow: hidden;
                    display: flex;
                    flex-direction: row;
                    justify-content: space-between;
                    fill: var(--secondary-color);
                    font-size: smaller;
                }

               .buttons {
                    width: 118px;
                    margin-left: 10px;
                    display: flex;
                    flex-direction: row;
                    justify-content: space-between;
                }

                .buttons-single {
                    justify-content: flex-end;
                    width: 100%;
                }

                .buttons > div {
                    width: 26px;
                    height: 26px;
                    border-radius: 15%;
                    padding: 1px;
                    cursor: pointer;
                    margin: 0 8px;
                }

                .buttons > div:hover {
                    background-color: var(--primary-color-variant2);
                }

                .labels-wrapper {
                    display: flex;
                    position: absolute;
                    top: -3px;
                    right: 5px;
                }

                .labels-wrapper div {
                    color: white;
                    padding: 2px 5px;
                    margin-left: 5px;
                    font-size: smaller;
                }

                .cover-label {
                    background-color: var(--secondary-color);
                }

                .report-label {
                    background-color: #AB2F00;
                }

                .empty-icon {
                    display: flex;
                    justify-content: center;
                    align-content: center;
                    align-items: center;
                    height: 100%;
                }

                .empty-icon svg {
                    width: 50px;
                    height: 50px;
                    fill: var(--secondary-color-variant1);
                }
                
                .confirmation-dialog {
					position: absolute;
					top: 50%;
					left: 50%;
					padding: 10px;
					width: 400px;
					height: 300px;
					background-color: #fff;
					transform: translate(-50%, -50%);
					box-shadow: 0 2px 2px 0 rgba(0, 0, 0, 0.14), 0 1px 5px 0 rgba(0, 0, 0, 0.12), 0 3px 1px -2px rgba(0, 0, 0, 0.2);
				}
            </style>

            ${until(this._renderImage(), html`<span> Loading...</span>`)}`;
	}

	_renderImage() {
		return this.getImage(this.src).then(() => html`
            <div class='img' style="background-image: url('${this.image.url}');" @click="${() => this._openImg()}">
                <div class="labels-wrapper" >
                    <div class="cover-label" ?hidden="${!this.isCover || !this.isInspectionPage}">Cover</div>
                    <div class="report-label" ?hidden="${!this.notInReportImages || !this.notInReportImages.includes(this.image.id)}">Not in report</div>
                </div>
            </div>
            <div class='f' ?hidden="${this.hideInfo}">
                <div class="img-name">${this._getImgName()}</div>
                <div class="img-options">
                    <div>
                        ${(this.image.description ? this.image.description.substring(0, 20) + "..." : "")}
                    </div>
                    <div ?hidden="${this.hideContext}">
                        ${this.image.context}
                    </div>
                    ${this.isRemote ? "" : html`
                    <div class="buttons">
                        <div @click="${() => this.openConfirmationDialog()}" title="delete">${deleteForever}</div>
						${this.drawing && this.image.propertyName === 'signature' ? '' : html`
                        <div @click="${() => this._editDescription()}" title="edit description">${description}</div>         
                        <div @click="${() => this.editDrawing()}" title="edit in drawing">${brush}</div>
						`}
                    </div>`}     
                </div>
            </div>`, () => html`
            <div class="img empty-icon">
                ${image}
            </div>
            <div class='f' ?hidden="${this.hideInfo}">
            <div class="img-name"></div>
            <div class="img-options">
                <div class="buttons buttons-single">
                    <div @click="${() => this.openConfirmationDialog()}">${deleteForever}</div>
                </div>
            </div>
        </div>`);
	}

	_getImgName() {
		return this.drawing
			? this.image.propertyName
			: ((!this.hideContext && this.context) ? this.context + " - " + this.image.propertyName : this.image.propertyName)
	}

	static get properties() {
		return {
			isRemote: { type: Boolean },
			src: { type: String },
			hideInfo: { type: Boolean },
			hideContext: { type: Boolean },
			drawing: { type: Boolean },
			isCover: { type: Boolean },
			isInspectionPage: { type: Boolean },
			context: { type: String },
			notInReportImages: { type: Array },
			applyFunc: { type: Function }
		};
	}

	connectedCallback() {
		super.connectedCallback();
		if (!this.isRemote) {
			this.__keyDown = this._keyDown.bind(this);
			document.addEventListener('keydown', this.__keyDown);
			this.__subscribed = true;
		}
	}

	disconnectedCallback() {
		super.disconnectedCallback();
		if (this.image && this.image.url) {
			URL.revokeObjectURL(this.image.url);
		}
		if (this.__subscribed) {
			document.removeEventListener('keydown', this.__keyDown);
		}
	}

	_keyDown(e) {
		if (e.keyCode === 27 && this.popupOpen) {
			this.popup.closeFunc();
		}
	}

	_openImg() {
		if (this.isCover && this.hideInfo) return;

		var v = document.createElement("insp-photo-viewer");
		v.url = this.image.url;
		v.imgId = this.image.id;
		v.drawing = this.drawing;
		v.cover = this.isCover;
		v.name = this.image.propertyName;
		v.description = this.image.description
		v.notInReportImages = this.notInReportImages
		v.applyFunc = this.applyFunc
		v.isRemote = this.isRemote

		document.body.appendChild(v);
	}

	__openDrawingTool(id) {
		var dt = document.createElement("inspx-drawing-tool");
		if (id) {
			if (this.drawing) {
				dt.drawingIdToLoad = id;
			} else {
				dt.imageIdToLoad = id;
			}
		}
		document.body.appendChild(dt);
	}

	editDrawing() {
		this.__openDrawingTool(this.image.id);
	}

	getImage(s) {
		if (s == undefined) return Promise.resolve({});

		let getter = null;
		if (this.isRemote) {
			if (!this.drawing)
				getter = (key) => Promise.resolve(INSP.APP.state.inspection.remote.images.find(i => i.id == key));
			else
				getter = (key) => Promise.resolve(INSP.APP.state.inspection.remote.drawings.find(i => i.id == key));
		} else if (!this.isRemote) {
			if (!this.drawing)
				getter = getImageById;
			else
				getter = getDrawingById;
		}

		return getter(s).then(a => {
			this.image = this._getProjection(a);
		});
	}

	_getProjection(i) {
		return {
			url: this._createImageUrl(i),
			date: new Date(i.date).toLocaleDateString(),
			id: i.id,
			context: i.context,
			isHorizontal: i.isHorizontal,
			name: i.name,
			propertyName: i.propertyName,
			description: i.description
		};
	}

	_createImageUrl(img) {
		if (img.data) {
			const blob = new Blob([img.data], { type: img.dataMimeType || "image/webp" });
			return URL.createObjectURL(blob);
		} else if (img.dataArray) {
			const blob = new Blob([new Uint8Array(img.dataArray)], { type: img.dataMimeType || "image/webp" });
			return URL.createObjectURL(blob);
		}
	}

	openConfirmationDialog() {
		let dialog = new InspxDialog();
		dialog.cancellable = true;
		dialog.content = 'Are you sure you want to delete the image?';
		dialog.addEventListener('dialog-accept', (e) => {
			this.deletePhotoOrDrawing();
			dialog.remove();
		});
		dialog.addEventListener('dialog-cancel', (e) => {
			dialog.remove();
		});
		this.shadowRoot.appendChild(dialog);
		dialog.open();
	}

	_editDescription() {
		this.popupOpen = true
		this.popup = new ImageSettingsPopup(
			this.image.propertyName, this.image.description, this.image.id, this.applyFunc, this.drawing);
		this.popup.applyCallback = (name, description) => {
			this.image.propertyName = name;
			this.image.description = description;
		};
		this.popup.closeCallback = () => {
			this.popupOpen = false;
		};
		document.body.appendChild(this.popup);
	}

	deletePhotoOrDrawing() {
		this.dispatchEvent(
			new CustomEvent(this.drawing ? "drawing-delete" : "photo-delete", {
				detail: {
					id: this.image ? this.image.id : this.src,
					el: this
				},
				composed: true
			})
		);
		this.showReset = false;
	}
}

window.customElements.define("inspx-db-image", InspxDbImage);
